import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { FiUser, FiMail, FiPhone, FiLock, FiCalendar, FiCheckCircle } from 'react-icons/fi';

export const Register: React.FC = () => {
  // Calculate default date of birth (18 years ago from today)
  const defaultDateOfBirth = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date.toISOString().split('T')[0];
  }, []);

  // Calculate max date of birth (must be at least 18 years old)
  const maxDateOfBirth = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    return date.toISOString().split('T')[0];
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    date_of_birth: defaultDateOfBirth,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  // Format phone number as user types
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Handle empty input
    if (!digits) return '';

    // If starts with 234, format as +234 XXX XXX XXXX
    if (digits.startsWith('234')) {
      const formatted = digits.slice(0, 13); // Limit to 13 digits
      if (formatted.length <= 3) return `+${formatted}`;
      if (formatted.length <= 6) return `+${formatted.slice(0, 3)} ${formatted.slice(3)}`;
      if (formatted.length <= 9) return `+${formatted.slice(0, 3)} ${formatted.slice(3, 6)} ${formatted.slice(6)}`;
      return `+${formatted.slice(0, 3)} ${formatted.slice(3, 6)} ${formatted.slice(6, 9)} ${formatted.slice(9)}`;
    }

    // If starts with 0, format as 0XXX XXX XXXX
    if (digits.startsWith('0')) {
      const formatted = digits.slice(0, 11); // Limit to 11 digits
      if (formatted.length <= 4) return formatted;
      if (formatted.length <= 7) return `${formatted.slice(0, 4)} ${formatted.slice(4)}`;
      return `${formatted.slice(0, 4)} ${formatted.slice(4, 7)} ${formatted.slice(7)}`;
    }

    // If starts with other digits, auto-prepend +234
    const formatted = digits.slice(0, 10); // Limit to 10 digits
    if (formatted.length <= 3) return `+234 ${formatted}`;
    if (formatted.length <= 6) return `+234 ${formatted.slice(0, 3)} ${formatted.slice(3)}`;
    return `+234 ${formatted.slice(0, 3)} ${formatted.slice(3, 6)} ${formatted.slice(6)}`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Special handling for phone number
    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData({
        ...formData,
        phone: formattedPhone,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registrationData } = formData;
      // Sanitize phone number (remove spaces and dashes)
      const sanitizedData = {
        ...registrationData,
        phone: registrationData.phone.replace(/[\s\-()]/g, ''),
      };
      await register(sanitizedData);
      navigate('/customer/dashboard');
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Fast loan approval process',
    'Competitive interest rates',
    'Flexible repayment options',
    'Secure & encrypted platform'
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ width: '100%', maxWidth: '1200px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem', alignItems: 'center' }}>

        {/* Left Side - Branding & Features */}
        <div style={{ color: 'white' }} className="hidden lg:block">
          <div style={{ marginBottom: '3rem' }}>
            <h1 style={{ fontSize: '3rem', fontWeight: 'bold', marginBottom: '1rem', lineHeight: '1.2' }}>
              Welcome to Coin MFI
            </h1>
            <p style={{ fontSize: '1.25rem', opacity: 0.95, lineHeight: '1.6' }}>
              Fast, secure microfinance loans at your fingertips
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {features.map((feature, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: '2.5rem', height: '2.5rem', background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(10px)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiCheckCircle size={20} />
                </div>
                <span style={{ fontSize: '1.125rem', fontWeight: '500' }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div style={{ background: 'white', borderRadius: '1.5rem', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', overflow: 'hidden' }}>
          <div style={{ padding: '2.5rem' }}>
            <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
              <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>Create Account</h2>
              <p style={{ color: '#6b7280' }}>Join thousands of customers getting instant loans</p>
            </div>

            {error && <ErrorMessage message={error} onClose={() => setError('')} />}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    First Name
                  </label>
                  <div style={{ position: 'relative' }}>
                    <FiUser style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      placeholder="John"
                      disabled={loading}
                      autoComplete="given-name"
                      style={{ width: '100%', paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', transition: 'all 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Last Name
                  </label>
                  <div style={{ position: 'relative' }}>
                    <FiUser style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      placeholder="Doe"
                      disabled={loading}
                      autoComplete="family-name"
                      style={{ width: '100%', paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', transition: 'all 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Middle Name <span style={{ color: '#9ca3af', fontWeight: '400' }}>(Optional)</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <FiUser style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
                  <input
                    type="text"
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleChange}
                    placeholder="Middle name"
                    disabled={loading}
                    autoComplete="additional-name"
                    style={{ width: '100%', paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', transition: 'all 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <FiMail style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    disabled={loading}
                    autoComplete="email"
                    style={{ width: '100%', paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', transition: 'all 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Phone Number
                </label>
                <div style={{ position: 'relative' }}>
                  <FiPhone style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="0801 234 5678"
                    disabled={loading}
                    autoComplete="tel"
                    style={{ width: '100%', paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', transition: 'all 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                <p style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: '#6b7280' }}>
                  Format: +234 XXX XXX XXXX or 0XXX XXX XXXX
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Date of Birth <span style={{ color: '#9ca3af', fontWeight: '400' }}>(Must be 18+)</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <FiCalendar style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
                  <input
                    type="date"
                    name="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    max={maxDateOfBirth}
                    disabled={loading}
                    autoComplete="bday"
                    style={{ width: '100%', paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', transition: 'all 0.2s' }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <FiLock style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={8}
                      placeholder="••••••••"
                      disabled={loading}
                      autoComplete="new-password"
                      style={{ width: '100%', paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', transition: 'all 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  <p style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    Minimum 8 characters
                  </p>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                    Confirm Password
                  </label>
                  <div style={{ position: 'relative' }}>
                    <FiLock style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={18} />
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      minLength={8}
                      placeholder="••••••••"
                      disabled={loading}
                      autoComplete="new-password"
                      style={{ width: '100%', paddingLeft: '2.75rem', paddingRight: '1rem', paddingTop: '0.75rem', paddingBottom: '0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', transition: 'all 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={loading}
                disabled={loading}
              >
                Create Account
              </Button>
            </form>

            <div style={{ marginTop: '1.5rem', textAlign: 'center', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Already have an account?{' '}
                <Link to="/login" style={{ color: '#667eea', fontWeight: '600', textDecoration: 'none' }}>
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .hidden.lg\\:block {
            display: none !important;
          }

          /* Main container becomes single column */
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
            gap: 2rem !important;
          }

          /* Form inputs grid */
          div[style*="grid-template-columns: 1fr 1fr"] > div:first-child > div {
            grid-template-columns: 1fr !important;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          /* Main wrapper */
          div[style*="minHeight: 100vh"] {
            padding: 1.5rem !important;
          }

          /* Main container */
          div[style*="maxWidth: 1200px"] {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }

          /* Form card */
          div[style*="background: white"][style*="borderRadius: 1.5rem"] > div {
            padding: 1.5rem !important;
          }

          /* Heading */
          h2[style*="fontSize: 2rem"] {
            font-size: 1.75rem !important;
          }

          /* Input grids */
          div[style*="gridTemplateColumns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }

          /* Input fields */
          input[style*="padding"] {
            padding: 0.875rem 1rem 0.875rem 2.75rem !important;
            font-size: 1rem !important;
          }

          /* Labels */
          label[style*="fontSize: 0.875rem"] {
            font-size: 0.8125rem !important;
          }

          /* Button */
          button[type="submit"] {
            padding: 0.875rem !important;
          }

          /* Features grid on left side */
          div[style*="flexDirection: column"][style*="gap: 1.5rem"] {
            gap: 1rem !important;
          }

          div[style*="flexDirection: column"][style*="gap: 1.5rem"] span {
            font-size: 1rem !important;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          div[style*="minHeight: 100vh"] {
            padding: 1rem !important;
          }

          div[style*="background: white"][style*="borderRadius: 1.5rem"] > div {
            padding: 1.25rem !important;
          }

          h2[style*="fontSize: 2rem"],
          h2[style*="fontSize: 1.75rem"] {
            font-size: 1.5rem !important;
          }

          h1[style*="fontSize: 3rem"] {
            font-size: 2rem !important;
          }

          p[style*="fontSize: 1.25rem"] {
            font-size: 1.0625rem !important;
          }

          input {
            font-size: 0.9375rem !important;
          }

          div[style*="width: 2.5rem"][style*="height: 2.5rem"] {
            width: 2rem !important;
            height: 2rem !important;
          }
        }
      `}</style>
    </div>
  );
};
